import { customElement, property, state } from 'lit/decorators.js';
import store from '../../../store/store';
import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';

import { selectUser } from '../../user-profile/user-profile.selectors';
import { BaseComponent } from '../../base-component/base-component';
import { PaymentOrderItemData } from '../order-completion.interface';
import { getRentalIsbn13, getRentalProduct } from '../rental-product/rental-product';
import { getProductIsbn, setProductAsync } from '../../product-card/product';
import { StoreState } from '../../../store/store.interface';
import { getSharpenProduct, getSharpenProductUrl } from '../sharpen-product/sharpen-product';
import {
  appendDurationForTunnelPurchaseFlow,
  DEFAULT_COVER_IMAGE,
  getImageSrcUrl,
  getProductTerm,
  getProductTitleWithoutEdition,
  getTunnelProductTerm,
  getTunnelProductTitleVerbose,
  isConnectProduct,
  isSharpenPlus,
} from '../../../common/utils/product-util';
import environment from '../../../common/environment';
import newTabIcon from '../../../common/icons/new-tab-icon';
import { loading, MessageTemplateId } from '../../../common/constants/api-constants';
import emit from '../../../common/events';
import { ItemType } from '../../product-card/product.interface';
import {
  getCartItemFormat,
  getOrderApplicationForAnalytics,
  getProductEndAccessDate,
} from '../../../common/utils/cart-util';
import { getOrderProductMetadata } from '../store/order-completion.selectors';
import { formattedAuthors } from '../../../common/utils/util';
import { AlertSize } from '../../alert/alert';
import { MessageType } from '../../alert/global-message.interface';
import { isMhGoSharpenOrder } from '../../sharpen/utils/sharpen.util';
import { isMhGoItem } from '../../../common/utils/order-util';
import { getSharpenOrderType } from '../../../common/utils/analytics-util';
import { SHARPEN_TUNNEL_LAUNCH_URL } from '../../upsell/constants/sharpen-page-type.constants';
import { isVirtualLabsItem, VIRTUAL_LABS } from '../../virtual-labs/utils/virtual-labs.util';
import { ariaLabelWithNewTab } from '../../../common/utils/a11y-util';
import { fetchAleksBundleDurationInDays, fetchAleksProductTitleVerbose, fetchSharpenCompanionBundleDurationInDays, isAleksSharpenCompanionBundle as isAleksSharpenBundle } from '../../../common/utils/aleks-sharpen-bundle-util';
import { SharpenUpsellPromo } from '../../upsell/interfaces/sharpen-upsell.interface';

@customElement('mhecomm-tunnel-order-confirmation')
export class TunnelOrderConfirmation extends connect(store)(BaseComponent) {
  @property()
  private orderNumber = '';

  @property({ type: Object })
  public sharpenUpsellPromo?: SharpenUpsellPromo;

  @property({ type: Object })
  public tunnelProduct?: PaymentOrderItemData;

  @property({ type: Object })
  public upsellProducts: PaymentOrderItemData[] = [];

  @state()
  private firstName: string | undefined;

  @state()
  private rentalIsbn13 = '';

  @state()
  private sharpenProductUrl = '';

  @state()
  private bs5ModalInstance;

  @state()
  private isModalVisible = false;

  @state()
  private flow = 'tunnel-purchase';

  @state()
  private productFormat = '';

  connectedCallback(): void {
    super.connectedCallback();
  }

  updated() {
    this.bs5ModalInstance = this.bsInit('#rentNowModal', window.bootstrap?.Modal)?.instance;
  }

  stateChanged(state: StoreState) {
    const user = selectUser(state);
    this.firstName = user?.givenName;
    const isbn10 = this.tunnelProduct?.isbn10;
    if (this.tunnelProduct && !!isbn10) {
      if (this.tunnelProduct?.rentalAvailable) {
        store.dispatch(getRentalProduct(isbn10));
      }

      store.dispatch(setProductAsync(this.tunnelProduct.businessUnit, isbn10.toString()));
      const isbn = getProductIsbn(state, isbn10.toString());
      if (isbn) {
        store.dispatch(getSharpenProduct(isbn));
      }
      this.rentalIsbn13 = getRentalIsbn13(state);
      this.sharpenProductUrl = getSharpenProductUrl(state);
    }
    this.updateProductFormat();
  }

  updateProductFormat(): void {
    const mapping = {
      [ItemType.connect]: 'Connect',
      [ItemType.ltia]: 'eBook',
      [ItemType.mhGo]: 'Digital',
      [ItemType.apr]: 'Digital',
      [ItemType.virtualLabs]: VIRTUAL_LABS,
    };

    if (
      this.tunnelProduct?.isConnectSharpenBundle ||
      this.tunnelProduct?.isSharpenCompanion ||
      this.tunnelProduct?.isSharpenAdvantage
    ) {
      switch (this.tunnelProduct?.itemType) {
        case ItemType.connect:
          this.productFormat = 'Connect + Sharpen';
          break;
        case ItemType.mhGo:
          this.productFormat = 'GO + Sharpen';
          break;
      }
      return;
    }

    this.productFormat =
      mapping[this.tunnelProduct?.itemType || this.tunnelProduct?.flow! || this.tunnelProduct?.application!] || '';
  }

  renderComp() {
    const product = this.tunnelProduct!;
    const isLooseleaf = product.looseLeafAvailable;
    const isRentbook = product.rentalAvailable;
    const isSharpen = !!this.sharpenProductUrl;
    const isSomeTileEnabled = isLooseleaf || isRentbook || isSharpen;
    const isConnect = isConnectProduct(product);
    const isMhGoSharpen = isMhGoSharpenOrder(product);
    const title = getTunnelProductTitleVerbose(product.title + (product.edition ? `, ${product.edition}` : ''), product);
    const term = getTunnelProductTerm(product?.term);
    const productTerm = getProductTerm({
      term,
      itemType: product?.itemType,
      offerType: product?.offerType,
    });
    const isVirtualLabs = isVirtualLabsItem(product);
    const isAleksSharpenCompanionBundle = isAleksSharpenBundle(product?.offerType, product.isSharpenCompanion);

    return html`
      <div class="container">
        <div class="row mx-0">
          <div class="col-12">
            <h1 class="my-4">
              Thank you for your order${this.firstName && this.firstName.length > 0 ? ', ' + this.firstName : nothing}!
            </h1>
            <p class="mb-0">
              Your order has been processed. Your order number is:
              <b data-automation-id="order-number">${this.orderNumber}</b>.
            </p>
            <p>
              You will receive an email summarizing your purchase shortly. If you do not receive an email within 5
              minutes, please check your junk and spam folders.
            </p>
          </div>
        </div>
        ${this.sharpenUpsellPromo
          ? html`<div class="row mx-0 pb-3">
              <mhecomm-sharpen-upsell-promo-banner .sharpenUpsellPromo="${this.sharpenUpsellPromo}"
              </mhecomm-sharpen-upsell-promo-banner>
            </div>`
          : nothing}
        <div class="row mb-5 mx-0">
          <div class="col-4 col-sm-3 col-md-2 col-lg-2">
            <img class="img-fluid" src="${getImageSrcUrl(product?.isbn10.toString())}" alt="" />
          </div>
          <div class="col-8 col-sm-9 col-md-10 col-lg-9 col-xl-8">
            ${isAleksSharpenCompanionBundle
              ? this.aleksSharpenCompanionMetadataCard(product)
              : html`
                  <h2 class="h5">
                    ${product.virtual
                      ? appendDurationForTunnelPurchaseFlow({
                          duration: product?.duration,
                          durationPeriod: product?.durationPeriod,
                          title,
                          term,
                        })
                      : title}
                  </h2>
                  <ul class="list-unstyled">
                    <li>
                      <span class="fw-bold">ISBN:</span> ${product.isbn10} ${product.isbn13 ? '| ' + product.isbn13 : nothing}
                    </li>
                    ${!isConnect && product.authors?.length > 0
                      ? html`<li><span class="fw-bold">By:</span> ${formattedAuthors(product.authors, ',', ', ', ', ')}</li>`
                      : nothing}
                    ${this.productFormat
                      ? html`<li><span class="fw-bold">Format:</span> ${this.productFormat}</li>`
                      : nothing}
                    ${productTerm
                      ? html` <li><span class="fw-bold">Access Until:</span> ${getProductEndAccessDate(productTerm)}</li>`
                      : nothing}
                    <li>
                      ${product.copyrightYear
                        ? html`<span class="fw-bold">Copyright:</span> ${product.copyrightYear}`
                        : nothing}
                    </li>
                    ${!isConnect && !isMhGoSharpen && !isVirtualLabs ? html`<li>${this.accessNowBtn(product, 'Access Now')}</li>` : nothing}
                  </ul>
                  ${this.getProductVerbiage(product)}
                  ${isMhGoSharpen || isVirtualLabs
                    ? html`${this.accessNowBtn(product, 'Access Now', 'btn btn-primary mt-2 w-100 w-sm-auto')}`
                    : nothing
                  }
                `}
          </div>
          ${isConnect
            ? html` <div class="offset-sm-3 offset-md-2 col-12 col-sm-9 col-md-9 col-lg-9">
                ${this.accessNowBtn(product, isConnect ? 'Complete Registration' : 'Access Now')}
              </div>`
            : nothing}
          ${this.sharpenUpsellPromo
            ? html`<div class="mt-3">
                <mhecomm-sharpen-legal-disclaimer
                  ?isSharpenPlus=${true}
                  ?plainSharpenLegalDisclaimer=${true}
                  sharpenLegalDisclaimerPrefix="*"
                  .analyticsApplication=${this.getOrderApplicationAnalytics()}
                  .analyticsOrderType=${this.getCartTypeFromOrderCompletion(store.getState() as StoreState)}>
                </mhecomm-sharpen-legal-disclaimer>
              </div>`
            : nothing}
        </div>

        ${this.renderUpsellProducts()}
        ${isSomeTileEnabled
          ? html` <div>
              <h2>Enhance Your Studies!</h2>
              <div class="row my-3">
                ${isLooseleaf
                  ? html`<div class="d-flex col-12 col-sm-12 col-md-4 mb-3" data-automation-id="buy-a-loose-leaf-copy">
                      <div class="col-12 border-top border-4 rounded shadow p-3 border-blue">
                        <h3 class="h5 text-center fw-bold">Buy a Loose-Leaf Copy</h3>
                        <div class="text-center">
                          <img
                            class="img-fluid"
                            src="/uxt/static/external/connect/looseleaf.webp"
                            alt="Buy a Loose Leaf Copy"
                          />
                        </div>
                        <ul class="mt-3">
                          <li>Access this offer from your Connect Class Page</li>
                          <li>Full text, in color, with binder-ready pages</li>
                          <li>Starting at $25</li>
                          <li>Free shipping (contiguous United States)</li>
                        </ul>
                        <p class="px-3 pt-3 mt-4">
                          <span class="fw-semibold">Note:</span> You need to
                          <button
                            class="btn btn-link p-0 text-decoration-underline text-body"
                            @click=${() =>
                              this.onCompleteRegistration(
                                'click-complete-registration-buy-looseleaf-order-confirmation',
                                product.launchObject
                              )}
                            data-automation-id="complete-registration-link"
                          >
                            Complete Registration
                          </button>
                          before purchasing a loose-leaf copy.
                        </p>
                      </div>
                    </div>`
                  : nothing}
                ${isRentbook
                  ? html`<div class="d-flex col-12 col-sm-12 col-md-4 mb-3" data-automation-id="rent-the-book">
                      <div class="col-12 border-top border-4 rounded shadow p-3  border-blue">
                        <h3 class="h5 text-center fw-bold">Rent the Book</h3>
                        <div class="text-center">
                          <img class="img-fluid" src="/uxt/static/external/connect/rentbook.webp" alt="Rent the Book" />
                        </div>
                        <ul class="mt-3">
                          <li>Rent for a fraction of the printed price</li>
                          <li>Complete bound text</li>
                          <li>No-hassle return with free shipping</li>
                        </ul>
                        <a
                          class="link-dark-underline px-3"
                          data-automation-id="how-rent-works-link"
                          target="_blank"
                          href=${environment().shopUrl + '/highered/how-does-this-work.html'}
                          @click=${this.howDoesRentBookWorkAnalytics}
                          aria-label="${ariaLabelWithNewTab('How does this work')}"
                          >How does this work?</a
                        >
                        ${this.rentalIsbn13
                          ? html`<button
                              class="btn btn-outline-primary w-100 mt-4"
                              data-bs-toggle="modal"
                              data-bs-target="#rentNowModal"
                              aria-label="Rent now, opens in a modal"
                              @click=${this.openRentbookModal}
                              data-automation-id="rent-now"
                            >
                              Rent Now ${newTabIcon}
                            </button>`
                          : loading({ m: 'mt-4', height: '2.5rem' })}
                        <div
                          class="modal fade"
                          id="rentNowModal"
                          tabindex="-1"
                          aria-hidden="${this.isModalVisible ? 'false' : 'true'}"
                          aria-labelledby="rentNowModalLabel"
                        >
                          <div class="modal-dialog">
                            <div class="modal-content">
                              <div class="modal-header">
                                <h2 class="modal-title fs-5" id="rentNowModalLabel">Rent Now</h2>
                                <button
                                  type="button"
                                  class="btn-close"
                                  data-bs-dismiss="modal"
                                  aria-label="Close"
                                  @click=${this.closeRentbookModal}
                                  data-automation-id="close-modal"
                                ></button>
                              </div>
                              <div class="modal-body">
                                <p>
                                  You will be taken to our preferred partner eCampus.com to complete your transaction.
                                </p>
                              </div>
                              <div class="modal-footer">
                                <button
                                  type="button"
                                  class="btn btn-outline-primary"
                                  data-bs-dismiss="modal"
                                  @click=${this.closeRentbookModal}
                                  data-automation-id="no-thanks"
                                >
                                  NO THANKS
                                </button>
                                <a
                                  type="button"
                                  class="btn btn-primary"
                                  aria-label="${ariaLabelWithNewTab('Go to eCampus')}"
                                  @click=${this.goToEcampus}
                                  href="${this.eCampusRentalUrl()}"
                                  target="_blank"
                                  data-automation-id="ok"
                                >
                                  OK
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>`
                  : nothing}
                ${isSharpen && !product.isConnectSharpenBundle && !product.isSharpenCompanion
                  ? html`<div
                      class="d-flex col-12 col-sm-12 col-md-4 mb-3"
                      data-automation-id="sharpen-study-guide-app"
                    >
                      <div class="col-12 border-top border-4 rounded shadow p-3 border-blue">
                        <h3 class="h5 text-center fw-bold">Sharpen: Study Guide App</h3>
                        <div class="text-center">
                          <img
                            class="img-fluid"
                            style="height: 124px;"
                            src="/uxt/static/external/connect/sharpen.webp"
                            alt="Free Companion Study App"
                          />
                        </div>
                        <ul class="mt-3">
                          <li>The complete, fast way to learn any subject</li>
                          <li>Get the study app for iOS or Android</li>
                          <li>Download today</li>
                        </ul>
                        <a
                          class="btn btn-outline-primary w-100 mt-5"
                          aria-label="${ariaLabelWithNewTab('Learn more about Sharpen')}"
                          href="${this.sharpenProductUrl}"
                          @click=${this.sharpenAnalytics}
                          target="_blank"
                          data-automation-id="learn-more"
                        >
                          Learn More
                        </a>
                      </div>
                    </div>`
                  : nothing}
              </div>
            </div>`
          : nothing}
      </div>
    `;
  }

  public gotosharpen() {
    emit({
      element: this,
      name: 'mhecomm-tunnel-order-confirmation-launch-sharpen',
      dataLayer: true,
      detail: { id: this.id },
    });
    window.open(SHARPEN_TUNNEL_LAUNCH_URL);
  }

  renderLaunchSharpenButton() {
    return html` <button
      type="button"
      class="btn btn-primary btn-md mt-4 mb-2 w-100 w-md-auto"
      @click=${this.gotosharpen}
      aria-label="${ariaLabelWithNewTab('Launch Sharpen')}"
      id="tunnel-gotosharpen"
    >
      Launch Sharpen
    </button>`;
  }

  renderSharpenPlusProductDetails(product: PaymentOrderItemData) {
    return html`
      <div class="row mb-5 mx-0">
        <div class="product-image col-3 col-lg-2">
          <img class="mw-100" src="${product.imageUrl! || DEFAULT_COVER_IMAGE}" alt="${product.title}" />
        </div>
        <div class="col-9 ps-2 position-relative">
          <div>
            <a class="fs-large fw-semibold text-decoration-none" href="${product.url || ''}">
              ${getProductTitleWithoutEdition({
                title: product.title,
                programSourceId: product?.programSourceId!,
                businessUnit: product.businessUnit,
                isVirtual: product.virtual ? true : false,
                formatTypeDesc: product?.formatTypeDescription!,
                duration: product?.duration!,
                durationPeriod: product?.durationPeriod!,
                ...(product?.offerType && { offerType: product?.offerType }),
              })}
            </a>
          </div>
          <div class="mt-2">
            <span class="fw-bold">Format:</span>
            ${getCartItemFormat(product.format, product.formatCode ?? '', true) || 'Digital'}
          </div>
          <div class="mt-2 mb-6">
            <span class="fw-bold">Access Through: </span>
            ${new Date(product.expirationDate ?? '').toLocaleDateString('en-US', {
              year: '2-digit',
              month: '2-digit',
              day: '2-digit',
            })}
          </div>
          <div class="d-none d-md-block">
            ${this.renderLaunchSharpenButton()}
          </div>
        </div>
        <div class="col-12 d-md-none">
          ${this.renderLaunchSharpenButton()}
        </div>
      </div>
    `;
  }

  hasSharpenUpsell() {
    return this.upsellProducts.some((product) => isSharpenPlus(product.programSourceId));
  }

  renderUpsellProducts() {
    if (this.hasSharpenUpsell()) {
      return this.renderSharpenPlusProductDetails(
        this.upsellProducts.find((product) => isSharpenPlus(product.programSourceId))!
      );
    }
    // Add other upsell logics later
    return nothing;
  }

  accessNowBtn(product: PaymentOrderItemData, btnText: string, style?: string) {
    return html`<button
      class="${style ?? 'btn btn-danger mt-3 w-100 w-sm-auto'}"
      @click=${() =>
        this.onCompleteRegistration('click-complete-registration-order-confirmation', product.launchObject)}
      data-automation-id="complete-registration"
    >
      ${btnText}
    </button>`;
  }

  aleksSharpenCompanionMetadataCard(product: PaymentOrderItemData) {
    return html`
      <div class="fw-bold fs-5 mb-0 pb-2">
        ${fetchAleksProductTitleVerbose(product?.metaOfferContentsMetadata)}
      </div>
      <div class="pb-2">
        <span>
          <strong class="pb-1">ISBN:</strong>
          ${product.isbn13}
        </span>
      </div>
      <div class="pb-2">
        <span>
          <strong class="pb-1">Format:</strong>
          ALEKS + Sharpen
        </span>
      </div>
      <div class="pb-2">
        <span>
          <strong class="pe-1">ALEKS Access Until:</strong>
          ${getProductEndAccessDate(fetchAleksBundleDurationInDays(product.metaOfferContentsMetadata)?.toString() || '')}
        </span>
      </div>
      <div class="pb-2">
        <span>
          <strong class="pe-1">Sharpen Access Until:</strong>
          ${getProductEndAccessDate(fetchSharpenCompanionBundleDurationInDays(product.metaOfferContentsMetadata)?.toString() || '')}
        </span>
      </div>
      <div>
        ${this.getProductVerbiage(product)}
      </div>
      <div>
        ${this.accessNowBtn(product, 'Access Now')}
      </div>
    `;
  }

  getProductVerbiage(product: PaymentOrderItemData) {
    return html`
      <mhecomm-product-verbiage
        .application=${product?.application}
        .flow="${product.flow}"
        .looseLeafAvailable=${product?.looseLeafAvailable}
        .hasEbook=${product?.hasEbook}
        .isConnectSharpenBundle=${product?.isConnectSharpenBundle}
        .isOrderConfirmation=${true}
        .isSharpenCompanion=${product?.isSharpenCompanion}
        .isSharpenAdvantage=${product?.isSharpenAdvantage}
        .isMhGoItem=${isMhGoItem(product)}
        .isVirtualLabs=${isVirtualLabsItem(product)}
        .isAleksSharpenCompanionBundle=${isAleksSharpenBundle(product?.offerType, product.isSharpenCompanion)}
        .analyticsApplication=${this.getOrderApplicationAnalytics()}
        .analyticsOrderType=${this.getCartTypeFromOrderCompletion(store.getState() as StoreState)}
      >
      </mhecomm-product-verbiage>
      ${isConnectProduct(product) ? this.connectActionRequiredAlert() : ''}
    `;
  }

  onCompleteRegistration(name, launchObject) {
    emit({
      element: this,
      name: name,
      detail: {
        flow: this.flow,
        application: this.getOrderApplicationAnalytics(),
        orderType: this.getCartTypeFromOrderCompletion(store.getState() as StoreState),
      },
    });
    if (launchObject.method === 'GET') {
      const url = new URL(launchObject.url);
      (launchObject.params || []).forEach(({ key, value }) => url.searchParams.append(key, value));
      window.location.href = url.toString();
    } else if (launchObject.method === 'POST') {
      const url = launchObject.url;
      const formEl = document.createElement('form');
      formEl.setAttribute('action', url);
      formEl.setAttribute('method', 'POST');
      launchObject.params.forEach((param) =>
        formEl.insertAdjacentHTML('beforeend', `<input type="hidden" name="${param.key}" value='${param.value}'>`)
      );
      document.body.appendChild(formEl);
      formEl.submit();
    }
  }

  connectActionRequiredAlert() {
    return html`
      <mhecomm-alert
        id="connect-confirmation-alert"
        type="${MessageType.warning}"
        size=${AlertSize.small}
        message=${MessageTemplateId.CONNECT_REQUIRED_ACTION_MESSAGE}
      >
      </mhecomm-alert>
    `;
  }

  openRentbookModal() {
    this.bs5ModalInstance.show();
    this.isModalVisible = true;
    emit({
      element: this,
      name: 'click-rent-now-order-confirmation',
      detail: {
        flow: this.flow,
        application: this.getOrderApplicationAnalytics(),
        orderType: this.getCartTypeFromOrderCompletion(store.getState() as StoreState),
      },
    });
  }

  howDoesRentBookWorkAnalytics() {
    emit({
      element: this,
      name: 'how-does-this-work-rentbook-order-confirmation',
      dataLayer: true,
      detail: {
        flow: this.flow,
        application: this.getOrderApplicationAnalytics(),
        orderType: this.getCartTypeFromOrderCompletion(store.getState() as StoreState),
      },
    });
  }

  closeRentbookModal() {
    this.bs5ModalInstance.hide();
    this.isModalVisible = false;
    emit({
      element: this,
      name: 'close-modal-rentbook-order-confirmation',
      detail: {
        flow: this.flow,
        application: this.getOrderApplicationAnalytics(),
        orderType: this.getCartTypeFromOrderCompletion(store.getState() as StoreState),
      },
    });
  }

  goToEcampus() {
    this.bs5ModalInstance.hide();
    this.isModalVisible = false;
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'go-to-ecampus-order-confirmation',
        label: 'Go to Ecampus',
        flow: this.flow,
        application: this.getOrderApplicationAnalytics(),
        orderType: this.getCartTypeFromOrderCompletion(store.getState() as StoreState),
      },
    });
  }

  eCampusRentalUrl(): string {
    return `https://www.ecampus.com/shoppingcart?action=add&item=${this.rentalIsbn13}&newused=R&term=180&utm_source=mcgrental&utm_campaign=textbook&utm_medium=partner&referrer=mcgraw`;
  }

  sharpenAnalytics() {
    emit({
      element: this,
      name: 'click-sharpen-order-confirmation',
      detail: {
        flow: this.flow,
        application: this.getOrderApplicationAnalytics(),
        orderType: this.getCartTypeFromOrderCompletion(store.getState() as StoreState),
      },
    });
  }

  getCartTypeFromOrderCompletion(state: StoreState) {
    const productMetadata = getOrderProductMetadata(state);
    return getSharpenOrderType({
      offerType: productMetadata.offerType,
      itemType: productMetadata.itemType,
      flow: productMetadata.flow,
      application: productMetadata.application,
      formatCode: productMetadata.format.formatCode,
      isConnectSharpenBundle: productMetadata.isSharpenCompanion,
      isSharpenCompanion: productMetadata.isSharpenCompanion,
      isSharpenAdvantage: productMetadata.isSharpenAdvantage,
    });
  }

  getOrderApplicationAnalytics() {
    return getOrderApplicationForAnalytics(getOrderProductMetadata(store.getState() as StoreState));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tunnel-order-confirmation': TunnelOrderConfirmation;
  }
}
